// Building a dataset, or adding tasks to one: filters over the frozen v2 tasks, a size, and a sample spread across
// type × complexity × repo (at most two tasks from a session). The size is read as the interval it buys on a gap
// and what running models on it would cost, from the averages so far.
import { esc, postStatus } from '../core'
import { msSelect, wireMs, type MsOpt } from '../task-replay'
import { DEFAULT_SD, RUN_MODELS, estimate, halfWidth, spread } from '../../shared/replay'
import { COMPLEXITY, TYPES, TYPE_COLORS, modelName, money, typeName } from './fmt'
import { forgetPool, loadPool, type Pool, type PoolTask } from './pool'

export interface Target { id: string; name: string; taskIds: string[]; models: string[] }

interface Form {
  target: Target | null // adding to a dataset; null: a new one
  name: string
  typeF: string[]
  cxF: string[]
  repoF: string[]
  size: number
  models: string[]
  submitting: boolean
  error: string
}

const SIZES = [30, 60, 120, 240]
const MODAL = 'rp-builder'

let form: Form | null = null
let pool: Pool | null = null

/** Open the builder: a new dataset, or more tasks for `target`. `done` gets the dataset's id once it is saved. */
export function openBuilder(target: Target | null, done: (id: string, added: number) => void): void {
  form = { target, name: '', typeF: [], cxF: [], repoF: [], size: target ? 30 : 60, models: [], submitting: false, error: '' }
  onDone = done
  paint()
  loadPool()
    .then((p) => {
      pool = p
      if (!form) return
      form.models = target?.models.length ? target.models : (p.planning.models.length ? p.planning.models : RUN_MODELS).slice(0, 3)
      paint()
    })
    .catch(() => { if (form) { form.error = 'Could not load the task pool.'; paint() } })
}

let onDone: (id: string, added: number) => void = () => {}

function close(): void {
  form = null
  document.getElementById(MODAL)?.remove()
}

/** The dataset's own tasks, when adding to one. */
function inDataset(): PoolTask[] {
  const have = new Set(form?.target?.taskIds ?? [])
  return (pool?.tasks ?? []).filter((t) => have.has(t.id))
}

/** The tasks a dataset can take: every frozen v2 task, less those it has. */
function candidates(): PoolTask[] {
  const have = new Set(form?.target?.taskIds ?? [])
  return (pool?.tasks ?? []).filter((t) => !have.has(t.id))
}

function filtered(): PoolTask[] {
  const f = form!
  const pass = (sel: string[], v: string) => !sel.length || sel.indexOf(v) >= 0
  return candidates().filter((t) => pass(f.typeF, t.taskType) && pass(f.cxF, t.complexity) && pass(f.repoF, t.repo))
}

function options(field: 'taskType' | 'complexity' | 'repo'): MsOpt[] {
  const vals = [...new Set(candidates().map((t) => t[field]))]
  if (field === 'taskType') {
    const rank = (v: string) => { const i = TYPES.findIndex((t) => t[0] === v); return i < 0 ? 99 : i }
    return vals.sort((a, b) => rank(a) - rank(b)).map((v) => ({ v, label: typeName(v) }))
  }
  if (field === 'complexity') {
    const rank = (v: string) => { const i = COMPLEXITY.indexOf(v); return i < 0 ? 99 : i }
    return vals.sort((a, b) => rank(a) - rank(b)).map((v) => ({ v, label: v }))
  }
  return vals.sort().map((v) => ({ v, label: v || '(none)' }))
}

function paint(): void {
  if (!form) return
  let el = document.getElementById(MODAL)
  if (!el) {
    el = document.createElement('div')
    el.id = MODAL
    el.className = 'tr-modal-backdrop'
    document.body.appendChild(el)
  }
  const f = form
  const title = f.target ? 'Add tasks to ' + esc(f.target.name) : 'New dataset'
  if (!pool) {
    el.innerHTML = '<div class="tr-modal tr-sb-modal"><div class="tr-modal-head tr-sb-head"><div><h2>' + title + '</h2></div>' +
      '<button type="button" class="tr-modal-x" data-x>✕</button></div>' +
      (f.error ? '<div class="notice tr-dlg-err tr-sb-err">' + esc(f.error) + '</div>' : '<div class="empty">Loading the task pool…</div>') + '</div>'
    wireClose(el)
    return
  }
  const ms = (id: string, label: string, all: string, noun: string, opts: MsOpt[], sel: string[]) =>
    msSelect(id, label, all, noun, opts, sel, { dropUnknown: true })
  el.innerHTML = '<div class="tr-modal tr-sb-modal rp-bd">' +
    '<div class="tr-modal-head tr-sb-head"><div><h2>' + title + '</h2></div><button type="button" class="tr-modal-x" data-x>✕</button></div>' +
    (f.error ? '<div class="notice tr-dlg-err tr-sb-err">' + esc(f.error) + '</div>' : '') +
    '<div class="tr-sb-body">' +
    (f.target ? '' : '<label class="tr-fld"><span>Name</span><input id="rpBdName" type="text" value="' + esc(f.name) + '"/></label>') +
    '<div class="tr-sb-filters">' +
    ms('rpBdType', 'Task type', 'All task types', 'types', options('taskType'), f.typeF) +
    ms('rpBdCx', 'Complexity', 'All complexities', 'levels', options('complexity'), f.cxF) +
    ms('rpBdRepo', 'Repo', 'All repos', 'repos', options('repo'), f.repoF) +
    '</div>' +
    '<div id="rpBdLive">' + live() + '</div>' +
    '</div>' +
    '<div class="tr-modal-foot tr-sb-foot"><div class="tr-sb-note" id="rpBdNote"></div>' +
    '<div class="tr-sb-acts"><button type="button" class="btn tr-cancel" data-x>Cancel</button>' +
    '<button type="button" class="btn tr-launch" id="rpBdGo"></button></div></div></div>'
  wireClose(el)
  const name = el.querySelector('#rpBdName') as HTMLInputElement | null
  if (name) name.oninput = () => { f.name = name.value; syncGo(el!) }
  const facet = (id: string, key: 'typeF' | 'cxF' | 'repoF') =>
    wireMs(el!, id, (vals) => { if (!form) return; form[key] = vals; refresh(el!) }, { live: true })
  facet('rpBdType', 'typeF')
  facet('rpBdCx', 'cxF')
  facet('rpBdRepo', 'repoF')
  ;(el.querySelector('#rpBdGo') as HTMLElement).onclick = () => submit(el!)
  wireLive(el)
  syncGo(el)
}

function wireClose(el: HTMLElement): void {
  Array.prototype.forEach.call(el.querySelectorAll('[data-x]'), (b: HTMLElement) => { b.onclick = close })
  el.onclick = (e) => { if (e.target === el) close() }
}

function refresh(el: HTMLElement): void {
  ;(el.querySelector('#rpBdLive') as HTMLElement).innerHTML = live()
  wireLive(el)
  syncGo(el)
}

function sample(): PoolTask[] {
  const all = filtered()
  return spread(all, Math.min(form!.size, all.length))
}

/** The size, what it buys and costs, and the sample's make-up: repainted on every change. */
function live(): string {
  const f = form!
  const all = filtered()
  const picked = sample()
  const sd = pool!.planning.sd ?? DEFAULT_SD
  const before = f.target?.taskIds.length ?? 0
  const half = (n: number) => '±' + Math.round(halfWidth(n, sd))
  const chips = [...SIZES.filter((n) => n < all.length), all.length].map((n) =>
    '<button type="button" class="rp-size' + (n === Math.min(f.size, all.length) ? ' on' : '') + '" data-size="' + n + '">' +
    '<b>' + (n === all.length ? 'all ' + n : n) + '</b><span>' + half(before + n) + '</span></button>').join('')
  const est = estimate(picked, f.models, pool!.planning)
  const models = [...new Set([...f.models, ...pool!.planning.models, ...RUN_MODELS, ...(f.target?.models ?? [])])]
  const tile = (k: string, v: string, sub = '') => '<div class="rp-tile"><div class="rp-tile-k">' + esc(k) + '</div><div class="rp-tile-v">' + v + '</div>' +
    (sub ? '<div class="rp-tile-s">' + sub + '</div>' : '') + '</div>'
  return '<div class="tr-sb-tally"><span><b>' + all.length + '</b> tasks match' + (before ? ' (not in the dataset)' : '') + '</span></div>' +
    '<div class="rp-sizes"><span class="rp-sizes-l">Tasks</span>' + chips +
    '<input type="number" class="tr-filter rp-size-n" id="rpBdN" min="1" max="' + all.length + '" value="' + Math.min(f.size, all.length) + '"></div>' +
    '<div class="rp-tiles">' +
    tile(before ? 'Dataset' : 'Tasks', before ? before + ' → ' + (before + picked.length) : String(picked.length)) +
    tile('Gap interval', half(before + picked.length) + ' Elo', before ? 'now ' + half(before) : '95%, whole dataset') +
    tile('Slices with ' + SLICE_MIN + '+ tasks', String(slices([...inDataset(), ...picked])), 'type, complexity, repo, type × complexity') +
    tile('Cost to run', money(est.total), 'agents ' + money(Object.values(est.agent).reduce<number>((a, v) => a + (v ?? 0), 0)) +
      ' · grading ' + money(est.grading) + ' · judging ' + money(est.judging)) +
    '</div>' +
    '<div class="rp-models"><span class="rp-sizes-l">Cost for</span>' + models.map((m) =>
      '<label class="rp-mchip' + (f.models.indexOf(m) >= 0 ? ' on' : '') + '"><input type="checkbox" data-model="' + esc(m) + '"' +
      (f.models.indexOf(m) >= 0 ? ' checked' : '') + '>' + esc(modelName(m)) +
      (est.agent[m] === null ? ' <span class="tr-muted">(no runs yet)</span>' : '') + '</label>').join('') + '</div>' +
    composition(picked)
}

/** The tasks a slice needs before routing weighs it (replay/routing.py MIN_TASKS). */
const SLICE_MIN = 10

/** How many slices a rule could be read on: each type, complexity, repo and type × complexity with SLICE_MIN tasks. */
function slices(picked: PoolTask[]): number {
  const n = new Map<string, number>()
  const add = (k: string) => n.set(k, (n.get(k) ?? 0) + 1)
  for (const t of picked) {
    add('t:' + t.taskType)
    add('c:' + t.complexity)
    add('r:' + t.repo)
    add('tc:' + t.taskType + '|' + t.complexity)
  }
  return [...n.values()].filter((v) => v >= SLICE_MIN).length
}

function composition(picked: PoolTask[]): string {
  if (!picked.length) return '<div class="empty">No tasks match these filters.</div>'
  const types = TYPES.map((t) => t[0]).filter((t) => picked.some((x) => x.taskType === t))
  const cxs = COMPLEXITY.filter((c) => picked.some((x) => x.complexity === c))
  const repos = new Set(picked.map((t) => t.repo)).size
  const sessions = new Set(picked.map((t) => t.session)).size
  return '<div class="rp-bd-comp"><div class="rp-bd-comp-h">Sample · ' + repos + ' repos · ' + sessions + ' sessions</div>' +
    '<table class="tr-lb2"><thead><tr><th>Task type</th>' + cxs.map((c) => '<th class="r">' + esc(c) + '</th>').join('') +
    '<th class="r">All</th></tr></thead><tbody>' +
    types.map((t) => '<tr><td><span class="tr-mix-sw" style="background:' + (TYPE_COLORS[t] ?? '#8a857a') + '"></span> ' + esc(typeName(t)) + '</td>' +
      cxs.map((c) => {
        const k = picked.filter((x) => x.taskType === t && x.complexity === c).length
        return '<td class="r' + (k ? '' : ' tr-muted') + '">' + (k || '·') + '</td>'
      }).join('') + '<td class="r">' + picked.filter((x) => x.taskType === t).length + '</td></tr>').join('') +
    '</tbody></table></div>'
}

function wireLive(el: HTMLElement): void {
  Array.prototype.forEach.call(el.querySelectorAll('.rp-size'), (b: HTMLElement) => {
    b.onclick = () => { if (form) { form.size = Number(b.getAttribute('data-size')); refresh(el) } }
  })
  const n = el.querySelector('#rpBdN') as HTMLInputElement | null
  if (n) n.onchange = () => { if (form && Number(n.value) > 0) { form.size = Math.round(Number(n.value)); refresh(el) } }
  Array.prototype.forEach.call(el.querySelectorAll('.rp-mchip input'), (cb: HTMLInputElement) => {
    cb.onchange = () => {
      if (!form) return
      const m = cb.getAttribute('data-model') || ''
      form.models = cb.checked ? [...form.models, m] : form.models.filter((x) => x !== m)
      refresh(el)
    }
  })
}

function syncGo(el: HTMLElement): void {
  const f = form!
  const b = el.querySelector('#rpBdGo') as HTMLButtonElement | null
  if (!b) return
  const n = sample().length
  b.disabled = f.submitting || !n || (!f.target && !f.name.trim())
  b.textContent = f.submitting ? 'Saving…' : f.target ? 'Add ' + n + ' tasks' : 'Create dataset'
  b.title = !f.target && !f.name.trim() ? 'Name the dataset' : !n ? 'No tasks match' : ''
}

function submit(el: HTMLElement): void {
  const f = form
  if (!f || f.submitting) return
  const ids = sample().map((t) => t.id)
  if (!ids.length) return
  f.submitting = true
  f.error = ''
  syncGo(el)
  const req = f.target
    ? postStatus('/api/replay/datasets/' + encodeURIComponent(f.target.id) + '/tasks', { tasks: ids })
    : postStatus('/api/replay/datasets', { name: f.name.trim(), tasks: ids })
  req.then((r: any) => {
    if (!form) return
    const id = f.target ? f.target.id : r.body?.datasetId
    if (r.status >= 200 && r.status < 300 && id) {
      forgetPool()
      close()
      onDone(id, f.target ? Number(r.body?.added ?? ids.length) : ids.length)
    } else {
      form.submitting = false
      form.error = r.body?.error || 'Saving failed.'
      paint()
    }
  }).catch(() => { if (form) { form.submitting = false; form.error = 'Saving failed. Is the server reachable?'; paint() } })
}
